import { THEME_OPTIONS, isDarkTheme, isTheme, type Theme } from "@/lib/themes";
import { createContext, useContext, useLayoutEffect, useState } from "react";

export type { Theme } from "@/lib/themes";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: "light",
  setTheme: () => null,
};

const themeClassNames = THEME_OPTIONS.map(({ id }) => `theme-${id}`);

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    const storedTheme = localStorage.getItem(storageKey);

    if (storedTheme === "system") {
      const migratedTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      localStorage.setItem(storageKey, migratedTheme);
      return migratedTheme;
    }

    return isTheme(storedTheme) ? storedTheme : defaultTheme;
  });

  useLayoutEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove("dark", ...themeClassNames);
    root.classList.add(`theme-${theme}`);
    root.dataset.theme = theme;
    if (isDarkTheme(theme)) root.classList.add("dark");
  }, [theme]);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  return useContext(ThemeProviderContext);
};
